import React, { useCallback, useContext, useRef } from 'react';

import { DefaultExtensionProvider } from '@atlaskit/editor-common/extensions';
import { ExtensionProvidersProp } from '@atlaskit/editor-core/editor';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';
import { DiffContent } from '@atlassian/bitkit-diff';

import { CodeSuggestionCard } from 'src/components/code-suggestions/code-suggestion-card';
import { codeSuggestionsPlugin } from 'src/components/code-suggestions/code-suggestions-toolbar-plugin';
import { getCodeSuggestionsManifest } from 'src/components/code-suggestions/manifest';
import { ApiCommentOrPlaceholder } from 'src/components/conversation-provider/types';
import { useStatsigGate } from 'src/contexts/feature-provider';

import { DiffFeaturesContext } from '../../components/diff-features-context';

interface CodeSuggestionsHook {
  extensionProviders?: ExtensionProvidersProp;
  codeSuggestionsExtensionHandler?: (
    commentId: number,
    apiComment?: ApiCommentOrPlaceholder
  ) => Record<string, (node: any) => JSX.Element>;
  editorPlugins?: [typeof codeSuggestionsPlugin, boolean][];
  getSuggestions?: () => Record<string, string>;
}

/**
 * Custom hook to manage the logic and behavior for code suggestions in the editor.
 *
 * This hook returns configurations necessary to integrate code suggestions in both
 * edit and view modes of the editor.
 *
 * The hook handles the following:
 * - **Extension Providers**: provider required for enabling code suggestions in edit mode.
 * - **Extension Handler**: handler required for enabling code suggestions in view mode.
 * - **Editor Plugins**: plugin that is shown in the editor toolbar to suggest via the UI.
 */
export const useCodeSuggestions = (
  diffLine?: DiffContent
): CodeSuggestionsHook => {
  const extensionProvidersRef = useRef<ExtensionProvidersProp>();
  const pluginsRef = useRef<[typeof codeSuggestionsPlugin, boolean][]>();
  const { hasCodeSuggestions } = useContext(DiffFeaturesContext);
  const isViewModeEnabled = useStatsigGate(
    StatsigFeatureKeys.codeSuggestionsViewMode
  );
  const isEditModeEnabled = useStatsigGate(
    StatsigFeatureKeys.codeSuggestionsEditMode
  );
  const suggestionsRef = useRef<Record<string, string>>({});

  const saveSuggestion = useCallback(
    (newSuggestion: Record<string, string>) => {
      suggestionsRef.current = { ...suggestionsRef.current, ...newSuggestion };
    },
    []
  );

  const getSuggestions = useCallback(() => suggestionsRef.current, []);

  if (!hasCodeSuggestions || !diffLine) {
    return {
      extensionProviders: undefined,
      codeSuggestionsExtensionHandler: undefined,
      editorPlugins: undefined,
      getSuggestions: undefined,
    };
  }

  let codeSuggestionsExtensionHandler: CodeSuggestionsHook['codeSuggestionsExtensionHandler'];

  if (isEditModeEnabled) {
    extensionProvidersRef.current = extensionProvidersRef.current ?? [
      new DefaultExtensionProvider([
        getCodeSuggestionsManifest(diffLine, saveSuggestion),
      ]),
    ];
    pluginsRef.current = pluginsRef.current ?? [[codeSuggestionsPlugin, true]];
  }

  if (isViewModeEnabled) {
    codeSuggestionsExtensionHandler = (commentId, apiComment) => ({
      'com.atlassian.bbc.code-suggestions': (node: any) => (
        <CodeSuggestionCard
          diffLine={diffLine}
          node={node}
          commentId={commentId}
          apiComment={apiComment}
        />
      ),
    });
  }

  return {
    extensionProviders: extensionProvidersRef.current,
    codeSuggestionsExtensionHandler,
    editorPlugins: pluginsRef.current,
    getSuggestions: isEditModeEnabled ? getSuggestions : undefined,
  };
};
